import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Flag, Handshake, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api, errCode, formatXAF } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { DamesBoard } from "@/components/DamesBoard";
import { SoundToggle } from "@/components/SoundToggle";
import { loadPrefs } from "@/lib/damesBoard";
import { playCapture, playSteps, playWin } from "@/lib/sound";
import { GameChat } from "@/components/GameChat";
import { BackToLobby } from "@/components/BackToLobby";

const fmt = (ms) => { const s = Math.ceil(ms / 1000); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`; };
const count = (b, c) => Object.values(b).filter((p) => p.toLowerCase() === c).length;

const Clock = ({ g, color, name, offset }) => {
  const [, setT] = useState(0);
  useEffect(() => { const i = setInterval(() => setT((x) => x + 1), 250); return () => clearInterval(i); }, []);
  const active = g.status === "in_progress" && g.turn === color && !g.first_move;
  let ms = g.clock_ms[color];
  if (active) ms -= Date.now() + offset - new Date(g.server_now).getTime();
  ms = Math.max(0, ms);
  const tone = !active ? "bg-white/5 text-slate-300" : ms < 10000 ? "bg-red-500 text-white" : ms < 30000 ? "bg-orange-500 text-[#0B0D12]" : "bg-amber-400 text-[#0B0D12]";
  const lost = 20 - count(g.board, color === "w" ? "b" : "w");
  return (
    <div className="flex items-center justify-between gap-3" data-testid={`dames-player-${color}`}>
      <div className="flex items-center gap-2">
        <span className={`h-7 w-7 rounded-full border ${color === "w" ? "border-[#cdbb95] bg-[#F3E9D2]" : "border-[#3a2c22] bg-[#17120E]"}`} />
        <span className="font-semibold">{name}</span>
        <span className="text-xs text-slate-400" data-testid={`dames-captured-${color}`}>+{Math.max(0, lost)}</span>
      </div>
      <span className={`rounded-xl px-3 py-1 font-mono text-lg font-bold transition-colors ${tone}`} data-testid={`dames-clock-${color}`}>{fmt(ms)}</span>
    </div>
  );
};

const End = ({ g }) => {
  const { t } = useI18n();
  const [boards, setBoards] = useState(null);
  const [i, setI] = useState(0);
  useEffect(() => { api.get(`/dames/games/${g.id}/replay`).then(({ data }) => { setBoards(data); setI(data.length - 1); }); }, [g.id]);
  const mine = g.settlement?.payouts?.[g.me] || 0;
  const won = g.result === g.my_color;
  return (
    <div className="space-y-4" data-testid="dames-end">
      <div className="surface p-5 text-center">
        <Trophy className={`mx-auto h-8 w-8 ${won ? "text-amber-400" : "text-slate-500"}`} />
        <p className="mt-2 font-display text-2xl font-extrabold" data-testid="dames-end-title">
          {g.status === "cancelled" ? t("dames.cancelled") : g.result === "draw" ? t("dames.draw") : won ? t("dames.youWin") : t("dames.youLose")}
        </p>
        <p className="text-sm text-slate-400" data-testid="dames-end-reason">{t(`dames.reason_${g.reason}`)}</p>
        {g.status === "finished" && (
          <>
            <p className="mt-2 text-lg font-bold text-amber-300" data-testid="dames-end-gain">{mine - g.stake >= 0 ? "+" : "-"}{formatXAF(Math.abs(mine - g.stake))}</p>
            <p className="mt-1 text-xs text-slate-400" data-testid="dames-end-summary">
              {t("dames.endSummary", { pot: formatXAF(g.settlement?.pot || g.pot), com: formatXAF(g.settlement?.commission || 0), pay: formatXAF(mine), stake: formatXAF(g.stake) })}
            </p>
          </>
        )}
      </div>
      {boards && (
        <>
          <DamesBoard board={boards[i].board} flipped={g.my_color === "b"} themeKey={loadPrefs().theme} coords={loadPrefs().coords} last={boards[i].last} disabled />
          <div className="flex items-center justify-between">
            <button type="button" onClick={() => setI(Math.max(0, i - 1))} disabled={!i} className="grid h-11 w-11 place-items-center rounded-full border border-white/10 disabled:opacity-40" data-testid="dames-replay-prev"><ChevronLeft className="h-5 w-5" /></button>
            <span className="font-mono text-xs text-slate-400" data-testid="dames-replay-label">{i ? `${i}. ${boards[i].last.notation}` : t("dames.startPos")}</span>
            <button type="button" onClick={() => setI(Math.min(boards.length - 1, i + 1))} disabled={i === boards.length - 1} className="grid h-11 w-11 place-items-center rounded-full border border-white/10 disabled:opacity-40" data-testid="dames-replay-next"><ChevronRight className="h-5 w-5" /></button>
          </div>
        </>
      )}
      <Link to="/lobby?game=dames" className="block text-center text-sm text-amber-300 underline" data-testid="dames-back-lobby">{t("dames.backLobby")}</Link>
    </div>
  );
};

export default function DamesGamePage() {
  const { id } = useParams();
  const { t, tErr } = useI18n();
  const qc = useQueryClient();
  const [g, setG] = useState(null);
  const [offset, setOffset] = useState(0);
  const prefs = loadPrefs();
  const seen = useRef(0);
  const done = useRef(false);
  const [missing, setMissing] = useState(false);
  const load = useCallback(() => api.get(`/dames/games/${id}`).then(({ data }) => {
    setOffset(new Date(data.server_now).getTime() - Date.now());
    const n = data.moves.length;
    if (seen.current && n > seen.current) {
      const m = data.moves[n - 1];
      if (m.captured.length) playCapture(); else playSteps(1);
    }
    seen.current = n;
    if (["finished", "cancelled"].includes(data.status)) { done.current = true; qc.invalidateQueries({ queryKey: ["wallet"] }); }
    setG((old) => { if (old?.status === "in_progress" && data.status === "finished" && data.result === data.my_color) playWin(); return data; });
  }).catch((e) => { if (e?.response?.status === 404) { done.current = true; setMissing(true); } }), [id, qc]);
  useEffect(() => { load(); const i = setInterval(() => { if (!done.current) load(); }, 1000); return () => clearInterval(i); }, [load]);
  const send = async (action, body) => {
    if (action === "resign" && !window.confirm(t("dames.resignConfirm"))) return;
    try { await api.post(`/dames/games/${id}/${action}`, body); load(); } catch (e) { toast.error(tErr(errCode(e))); }
  };
  if (missing) return <div className="py-24 text-center text-slate-400" data-testid="dames-game-missing">{t("dames.notFound")} <Link to="/lobby?game=dames" className="ml-2 text-amber-300 underline">{t("dames.backLobby")}</Link></div>;
  if (!g) return <div className="py-24 text-center text-slate-500">{t("common.loading")}</div>;
  if (["finished", "cancelled"].includes(g.status)) return <div className="mx-auto max-w-2xl space-y-3"><div className="flex items-center justify-between"><BackToLobby to="/lobby?game=dames" testId="dames-header-back" /><GameChat kind="dames" gid={id} /></div><End g={g} /></div>;
  const opp = g.my_color === "w" ? "b" : "w";
  const names = { w: g.white, b: g.black };
  const myTurn = g.turn === g.my_color;
  const startIn = Math.ceil((new Date(g.starts_at) - (Date.now() + offset)) / 1000);
  return (
    <div className="mx-auto max-w-2xl space-y-3" data-testid="dames-game-page">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-slate-400" data-testid="dames-stake-info">{t("dames.stakeInfo", { s: formatXAF(g.stake), gain: formatXAF(g.gain) })}</p>
        <div className="flex shrink-0 items-center gap-2"><GameChat kind="dames" gid={id} /><SoundToggle /></div>
      </div>
      <Clock g={g} color={opp} name={names[opp]} offset={offset} />
      {!g.opponent_online && <p className="text-center text-xs font-semibold text-orange-300" data-testid="dames-opp-offline">{t("dames.oppOffline")}</p>}
      {g.status === "starting" && <p className="text-center font-display text-2xl font-bold text-amber-300" data-testid="dames-countdown">{t("dames.startsIn", { s: Math.max(0, startIn) })}</p>}
      {g.must_capture && <p className="text-center text-sm font-semibold text-amber-300" data-testid="dames-must-capture">{t("dames.mustCapture")}</p>}
      <DamesBoard board={g.board} legal={g.legal} onMove={(path) => send("move", { path })} flipped={g.my_color === "b"} themeKey={prefs.theme} coords={prefs.coords}
        last={g.moves[g.moves.length - 1]} animKey={g.moves.length} disabled={!myTurn || g.status !== "in_progress"} />
      <Clock g={g} color={g.my_color} name={`${names[g.my_color]} (${t("dames.you")})`} offset={offset} />
      <p className="text-center text-sm" data-testid="dames-turn">{g.status === "starting" ? t("dames.getReady") : g.first_move ? t("dames.firstMove") : myTurn ? t("dames.yourTurn") : t("dames.oppTurn")}</p>
      {g.draw_offer_by && g.draw_offer_by !== g.me && (
        <div className="surface flex items-center justify-between gap-2 p-3 text-sm" data-testid="dames-draw-offer">
          <span>{t("dames.drawOffered")}</span>
          <div className="flex gap-2">
            <button type="button" onClick={() => send("draw-accept")} className="h-9 rounded-full bg-amber-500 px-3 font-bold text-[#0B0D12]" data-testid="dames-draw-accept">{t("dames.acceptDraw")}</button>
            <button type="button" onClick={() => send("draw-decline")} className="h-9 rounded-full border border-white/15 px-3" data-testid="dames-draw-decline">{t("dames.decline")}</button>
          </div>
        </div>
      )}
      {g.status === "in_progress" && (
        <div className="flex gap-2">
          <button type="button" onClick={() => send("draw-offer")} disabled={!myTurn || g.draw_offers_left <= 0 || g.draw_offer_by === g.me}
            className="flex h-11 flex-1 items-center justify-center gap-2 rounded-full border border-white/15 text-sm font-semibold disabled:opacity-40" data-testid="dames-draw-btn">
            <Handshake className="h-4 w-4" /> {t("dames.offerDraw")} ({g.draw_offers_left})
          </button>
          <button type="button" onClick={() => send("resign")} className="flex h-11 flex-1 items-center justify-center gap-2 rounded-full border border-red-400/40 text-sm font-semibold text-red-300" data-testid="dames-resign-btn">
            <Flag className="h-4 w-4" /> {t("dames.resign")}
          </button>
        </div>
      )}
    </div>
  );
}
